'use client'

import { useState, useMemo } from 'react'
import { AnimatedSection } from '@/ui/animated-section'
import { AddPersonDialog } from '@/entries'
import { AddTopicDialog } from '@/topics'
import { MetadataRow } from '../forms/MetadataRow'
import type { CaptureEntryType } from '../types'
import type { PersonWithLastInteraction } from '@/entries'
import type { FormStateReturn } from '../hooks/useFormState'
import type { FormHandlers } from '../hooks/useFormHandlers'
import type { Project } from '@/document'

interface FormMetadataSectionProps {
  show: boolean
  formState: FormStateReturn
  handlers: FormHandlers
  effectiveType: CaptureEntryType | null
  selectedPeople: PersonWithLastInteraction[]
  availablePeople: PersonWithLastInteraction[]
  isPeopleLoading: boolean
  availableTopics: Array<{ id: string; name: string }>
  availableProjects: Project[]
  /** When true, project is pre-set and cannot be changed */
  projectLocked?: boolean
  isLoading: boolean
}

/**
 * Metadata section: wraps MetadataRow + AddPerson/AddTopic dialogs.
 */
export function FormMetadataSection({
  show,
  formState,
  handlers,
  effectiveType,
  selectedPeople,
  availablePeople,
  isPeopleLoading,
  availableTopics,
  availableProjects,
  projectLocked,
  isLoading,
}: FormMetadataSectionProps) {
  const [showAddTopic, setShowAddTopic] = useState(false)

  const selectedProject = useMemo(() => {
    if (!formState.selectedProjectId) return null
    return (
      availableProjects.find((p) => p.id === formState.selectedProjectId) ??
      null
    )
  }, [availableProjects, formState.selectedProjectId])

  const projectOptions = availableProjects.map((p) => ({
    value: p.id,
    label: p.name,
  }))

  return (
    <>
      <AnimatedSection show={show} delay="50ms">
        <MetadataRow
          effectiveType={effectiveType}
          dueDate={formState.dueDate}
          onDueDateChange={(date) => {
            formState.markFieldModified('dueDate')
            formState.setDraftValue('dueDate', date)
          }}
          important={formState.important}
          onImportantChange={(v) => {
            formState.markFieldModified('important')
            formState.setDraftValue('important', v)
          }}
          selectedTopicIds={formState.selectedTopicIds}
          availableTopics={availableTopics}
          onTopicsChange={handlers.handleTopicsChange}
          onAddTopic={() => setShowAddTopic(true)}
          onCreateTopic={handlers.handleCreateTopic}
          selectedPeople={selectedPeople}
          availablePeople={availablePeople}
          onAddPerson={handlers.handleAddPerson}
          onRemovePerson={handlers.handleRemovePerson}
          isPeopleLoading={isPeopleLoading}
          onAddNewPerson={() => formState.setShowAddPerson(true)}
          suggestedPeople={formState.suggestedPeople}
          addingPerson={formState.addingPerson}
          onAddSuggestedPerson={handlers.handleAddSuggestedPerson}
          onDismissSuggestedPerson={handlers.handleDismissSuggestedPerson}
          selectedProject={selectedProject}
          projectOptions={projectOptions}
          onProjectChange={(value) => {
            formState.markFieldModified('project')
            formState.setDraftValue('selectedProjectId', value)
          }}
          projectLocked={projectLocked}
          disabled={isLoading}
        />
      </AnimatedSection>

      {/* Dialogs */}
      <AddPersonDialog
        open={formState.showAddPerson}
        onOpenChange={formState.setShowAddPerson}
      />
      <AddTopicDialog
        open={showAddTopic}
        onOpenChange={setShowAddTopic}
        onCreate={handlers.handleCreateTopic}
      />
    </>
  )
}
